import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Flex,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { useGlobalContext } from '../state/GlobalContext';
import { createShopifyAddress, saveSubShippingMethod } from '../api';

const UpdateAddressConfirmModal: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { subscriptionId, customerId, authToken, shopDomain } =
    useGlobalContext();
  const {
    data: {
      code,
      draftId,
      newDeliveryAddress,
      oldDeliveryAddress,
      newPrice,
      oldPrice,
    },
    loadPreviousFlowStep,
    onClose,
  } = useModalManagerContext();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const handleOnAcceptShipping = async () => {
    setIsLoading(true);

    try {
      const shipRes = await saveSubShippingMethod(
        customerId,
        subscriptionId,
        authToken,
        {
          draftId,
          deliveryAddress: newDeliveryAddress,
          shippingOptionCode: code,
        },
      );

      await createShopifyAddress({
        address: newDeliveryAddress,
        customerId,
        authToken,
        shopDomain,
      });
      handleAddressUpdate(shipRes);
      toast({
        isClosable: true,
        position: 'top',
        status: 'success',
        title: t('subscription.address_update_success'),
        variant: 'success',
      });
      onClose();
    } catch (error) {
      toast({
        isClosable: true,
        position: 'top',
        status: 'error',
        title: (
          <Text color="white">
            {t('cancelModal.error')}{' '}
            <Link onClick={handleOnAcceptShipping}>
              {t('cancelModal.tryAgain')}
            </Link>
          </Text>
        ),
        variant: 'error',
      });
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddressUpdate = (res: SaveShippingMethodResponse) => {
    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...(old as Subscription),
        FormattedDeliveryPrice: res.FormattedDeliveryPrice,
        DeliveryMethod: {
          ...(old as Subscription).DeliveryMethod,
          ...res.DeliveryMethod,
        },
      }),
    );
  };

  const getAddressContent = (address) => (
    <>
      {address?.name || `${address?.firstName} ${address?.lastName}`}
      <br />
      {address.address1} <br />
      {address.address2 ? (
        <>
          {address.address2}
          <br />
        </>
      ) : null}
      {address.city}, {address.provinceCode} {address.zip}
      <br />
      {address.country}
    </>
  );

  return (
    <>
      <ModalHeader>{t('cancelModal.address.confirmModalTitle')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {oldPrice !== newPrice ? (
          <Box mb={4} width="100%">
            <Text>
              {t('cancelModal.address.confirmTitle', { oldPrice, newPrice })}
            </Text>
          </Box>
        ) : null}
        <Flex>
          <Stack direction={['column', 'row']} spacing={4}>
            <Box width={{ base: '100%', md: '25%' }}>
              <Text fontSize="sm" fontWeight="bold">
                {t('cancelModal.address.newAddress')}
              </Text>
            </Box>
            <Box width={{ base: '100%', md: '25%' }}>
              <Text fontSize="sm">{getAddressContent(newDeliveryAddress)}</Text>
            </Box>
            <Box width={{ base: '100%', md: '25%' }}>
              <Text fontSize="sm" fontWeight="bold">
                {t('cancelModal.address.oldAddress')}
              </Text>
            </Box>
            <Box width={{ base: '100%', md: '25%' }}>
              <Text fontSize="sm">{getAddressContent(oldDeliveryAddress)}</Text>
            </Box>
          </Stack>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={loadPreviousFlowStep} variant="outlinedBig">
          {t('cancelModal.back')}
        </Button>
        <Button
          isLoading={isLoading}
          onClick={handleOnAcceptShipping}
          variant="blackBig"
        >
          {t(
            oldPrice !== newPrice
              ? 'cancelModal.address.confirmAcceptShipping'
              : 'titles.confirm',
          )}
        </Button>
      </ModalFooter>
    </>
  );
};

export default UpdateAddressConfirmModal;
